import { useMemo } from 'react';
import { Select, SelectItem, Input, Switch, Autocomplete, AutocompleteItem } from '@heroui/react';
import { resolveChannelCount, resolveDesignTraits } from '@estimator/shared';
import type { UpholsteryDesign, UpholsteryDesignType } from '@estimator/shared';
import { useUpholsteryDesigns } from '@/api/upholstery-designs';
import { toDesignTypeOptions } from '@/utils/upholstery-design-options';

// ═══════════════════════════════════════════════════
//  Shared upholstery design selector
// ═══════════════════════════════════════════════════
//
// Parameterized on its two axes because the run axis varies by product: an arm
// lays its design along DEPTH, while the base and both cushions use their
// WIDTH. Labels come from the caller so the helper text reads naturally.
//
// The three existing per-product selectors predate this one and still carry
// their own copies of the JSX below. They now derive field visibility and
// channel count the same way this one does, via resolveDesignTraits and
// resolveChannelCount — see docs/superpowers/specs/2026-08-13-base-upholstery-design.md.

interface UpholsteryDesignSelectorProps {
  design: UpholsteryDesign;
  onChange: (design: UpholsteryDesign) => void;
  /** The dimension the design is laid out along — width for base and cushions, depth for arms. */
  runDimensionInches: number;
  /** Lowercase noun for the run axis, e.g. "width". Used in helper text. */
  runDimensionLabel: string;
  /** The cross axis that horizontal channels divide. */
  crossDimensionInches: number;
  crossDimensionLabel: string;
  hardwareOptions: Array<{ id: string; name: string }>;
}

export default function UpholsteryDesignSelector({
  design,
  onChange,
  runDimensionInches,
  runDimensionLabel,
  crossDimensionInches,
  crossDimensionLabel,
  hardwareOptions,
}: UpholsteryDesignSelectorProps) {
  const updateField = <K extends keyof UpholsteryDesign>(key: K, value: UpholsteryDesign[K]) => {
    onChange({ ...design, [key]: value });
  };

  const { data: designCatalog } = useUpholsteryDesigns();
  const designTypeOptions = useMemo(
    () => toDesignTypeOptions(designCatalog, design.type),
    [designCatalog, design.type],
  );

  // Field visibility comes from the catalog row, not from a hardcoded key list.
  // resolveDesignTraits falls back to the seeded map, so the eight shipped
  // designs render exactly the same fields while the query is still pending.
  const traits = useMemo(
    () => resolveDesignTraits(design.type, designCatalog),
    [design.type, designCatalog],
  );
  const isChannel = traits.has_channels;
  const isButton = traits.has_buttons;
  const isTuftedChannel = traits.is_tufted;

  // Delegates to the same resolver the cost calc uses, so the preview here can
  // never disagree with the price. Passing an override of null keeps this the
  // pure automatic count regardless of what the user has typed.
  const autoChannelCount = useMemo(
    () => resolveChannelCount(
      { ...design, channel_count_override: null },
      runDimensionInches,
      crossDimensionInches,
      traits,
    ),
    [design, runDimensionInches, crossDimensionInches, traits],
  );

  const isManualChannels = (design.channel_count_override ?? 0) > 0;
  const effectiveChannelCount = isManualChannels
    ? (design.channel_count_override as number)
    : autoChannelCount;

  /** Which axis the current design type actually divides. */
  const dividedAxisLabel =
    traits.channel_axis === 'cross' ? crossDimensionLabel : runDimensionLabel;
  const dividedAxisInches =
    traits.channel_axis === 'cross' ? crossDimensionInches : runDimensionInches;

  return (
    <div className="space-y-3">
      <Select
        label="Design Type"
        selectedKeys={[design.type]}
        onSelectionChange={(keys) => {
          const val = Array.from(keys)[0] as UpholsteryDesignType;
          if (val) updateField('type', val);
        }}
        size="sm"
        classNames={{ trigger: 'bg-[var(--bg-panel)]' }}
      >
        {designTypeOptions.map((opt) => (
          <SelectItem key={opt.key}>{opt.label}</SelectItem>
        ))}
      </Select>

      {isChannel && (
        <div className="grid grid-cols-3 gap-3 items-end">
          <Input
            label="Channel Width"
            type="number"
            value={(design.channel_width ?? '').toString()}
            onValueChange={(v) => updateField('channel_width', v ? Number(v) : null)}
            size="sm"
            classNames={{ inputWrapper: 'bg-[var(--bg-panel)]' }}
            endContent={<span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>in</span>}
          />
          <div className="flex items-center gap-2 h-10">
            <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>Channel Mode:</span>
            <Switch
              size="sm"
              isSelected={isManualChannels}
              onValueChange={(checked) => {
                updateField(
                  'channel_count_override',
                  checked ? Math.max(1, autoChannelCount) : null,
                );
              }}
            >
              <span className="text-xs">{isManualChannels ? 'Manual' : 'Auto'}</span>
            </Switch>
          </div>
          {isManualChannels ? (
            <Input
              label="Channel Count"
              type="number"
              value={(design.channel_count_override ?? '').toString()}
              onValueChange={(v) => {
                if (v === '') {
                  updateField('channel_count_override', null);
                  return;
                }
                const n = Math.floor(Number(v));
                if (Number.isFinite(n) && n >= 1) {
                  updateField('channel_count_override', n);
                }
              }}
              size="sm"
              min={1}
              classNames={{ inputWrapper: 'bg-[var(--bg-panel)]' }}
            />
          ) : (
            <div>
              <div className="text-[10px] mb-1" style={{ color: 'var(--text-muted)' }}>Auto Channels</div>
              <div
                className="text-xs font-semibold px-2 py-1.5 rounded"
                style={{ backgroundColor: 'var(--bg-panel)', color: 'var(--text-primary)' }}
              >
                {autoChannelCount}
              </div>
            </div>
          )}
        </div>
      )}

      {isButton && (
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Button Tuftings per Foot"
            type="number"
            value={(design.button_tuftings_per_foot ?? '').toString()}
            onValueChange={(v) =>
              updateField('button_tuftings_per_foot', v ? Math.max(0, Math.floor(Number(v))) : null)
            }
            size="sm"
            classNames={{ inputWrapper: 'bg-[var(--bg-panel)]' }}
            endContent={<span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>/ft</span>}
          />
          <Autocomplete
            label="Button Hardware"
            selectedKey={design.button_hardware_id ?? undefined}
            onSelectionChange={(key) => updateField('button_hardware_id', (key as string) || null)}
            size="sm"
            placeholder="Select hardware..."
            inputProps={{ classNames: { inputWrapper: 'bg-[var(--bg-panel)]' } }}
          >
            {hardwareOptions.map((h) => (
              <AutocompleteItem key={h.id}>{h.name}</AutocompleteItem>
            ))}
          </Autocomplete>
        </div>
      )}

      {isTuftedChannel && (
        <Autocomplete
          label="Wire Hardware"
          selectedKey={design.wire_hardware_id ?? undefined}
          onSelectionChange={(key) => updateField('wire_hardware_id', (key as string) || null)}
          size="sm"
          placeholder="Select wire hardware..."
          inputProps={{ classNames: { inputWrapper: 'bg-[var(--bg-panel)]' } }}
        >
          {hardwareOptions.map((h) => (
            <AutocompleteItem key={h.id}>{h.name}</AutocompleteItem>
          ))}
        </Autocomplete>
      )}

      {isChannel && effectiveChannelCount > 0 && (
        <div
          className="text-[10px] px-2 py-1 rounded"
          style={{ backgroundColor: 'var(--bg-surface)', color: 'var(--text-secondary)' }}
        >
          {effectiveChannelCount} channel{effectiveChannelCount !== 1 ? 's' : ''} at{' '}
          {design.channel_width ?? 0}&quot; across the {dividedAxisInches}&quot; {dividedAxisLabel}
          {isManualChannels ? ' (manual)' : ''}
        </div>
      )}
    </div>
  );
}
